import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import SignaturePad from 'signature_pad';
import { Contract, ContractSnapshot, ContractSnapshotSection } from '../types';

export function SignContract() {
  const { token } = useParams<{ token: string }>();
  const [contract, setContract] = useState<Contract | null>(null);
  const [status, setStatus] = useState<'loading' | 'ready' | 'signed' | 'already_signed' | 'invalid'>('loading');
  const [typedName, setTypedName] = useState('');
  const [agreed, setAgreed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const padRef = useRef<SignaturePad | null>(null);

  useEffect(() => {
    if (!token) { setStatus('invalid'); return; }
    fetch(`/api/contract/${token}`)
      .then(async (res) => {
        if (!res.ok) { setStatus('invalid'); return; }
        const data = await res.json();
        if (!data || data.error) { setStatus('invalid'); return; }
        setContract(data as Contract);
        setStatus(data.contract_status === 'signed' ? 'already_signed' : 'ready');
      })
      .catch(() => setStatus('invalid'));
  }, [token]);

  useEffect(() => {
    if (status === 'ready' && canvasRef.current) {
      padRef.current = new SignaturePad(canvasRef.current, {
        backgroundColor: 'rgb(255,255,255)',
        penColor: '#1a1a1a',
      });
      const resize = () => {
        const canvas = canvasRef.current;
        if (!canvas || !padRef.current) return;
        const ratio = Math.max(window.devicePixelRatio || 1, 1);
        const rect = canvas.getBoundingClientRect();
        canvas.width = rect.width * ratio;
        canvas.height = rect.height * ratio;
        const ctx = canvas.getContext('2d');
        if (ctx) ctx.scale(ratio, ratio);
        padRef.current.clear();
      };
      resize();
      window.addEventListener('resize', resize);
      return () => window.removeEventListener('resize', resize);
    }
  }, [status]);

  const handleSubmit = async () => {
    if (!typedName.trim()) { setError('Please type your full legal name.'); return; }
    if (!padRef.current || padRef.current.isEmpty()) { setError('Please draw your signature below.'); return; }
    if (!agreed) { setError('You must check the agreement box to sign.'); return; }
    setSubmitting(true); setError('');
    try {
      const res = await fetch('/api/sign-contract', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          token,
          typedName: typedName.trim(),
          signatureData: padRef.current.toDataURL('image/png'),
          agreed: true,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data.error || 'There was a problem submitting. Please try again.');
        setSubmitting(false);
        return;
      }
      setStatus('signed');
    } catch (e) {
      setError('Network error. Please try again.');
      setSubmitting(false);
    }
  };

  const snapshot: ContractSnapshot | null = (contract as any)?.preview_snapshot ?? null;

  if (status === 'loading') return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="w-8 h-8 border-4 border-gold-500 border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (status === 'invalid') return (
    <div className="min-h-screen flex items-center justify-center px-4 text-center">
      <div>
        <h1 className="font-serif text-2xl font-bold text-gray-900 mb-2">Invalid Link</h1>
        <p className="text-gray-500">This contract link is invalid or has expired. Please contact Nuno Productions LLC.</p>
      </div>
    </div>
  );

  if (status === 'already_signed') return (
    <div className="min-h-screen flex items-center justify-center px-4 text-center">
      <div>
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h1 className="font-serif text-2xl font-bold text-gray-900 mb-2">Already Signed</h1>
        <p className="text-gray-500">This contract has already been signed. Thank you!</p>
        <a href={`/api/contract-pdf/${token}`}
          className="inline-block mt-4 bg-gold-500 hover:bg-gold-600 text-white px-5 py-2.5 rounded-xl text-sm font-semibold transition-colors">
          Download Signed PDF
        </a>
        <p className="text-sm text-gray-400 mt-3">— Nuno Productions LLC</p>
      </div>
    </div>
  );

  if (status === 'signed') return (
    <div className="min-h-screen flex items-center justify-center px-4 text-center">
      <div>
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h1 className="font-serif text-2xl font-bold text-gray-900 mb-3">You're all set!</h1>
        <p className="text-gray-600 max-w-xs mx-auto">
          Your contract for <strong>{snapshot?.event?.dateFormatted ?? '—'}</strong> has been signed.
          A copy has been emailed to you, and Justin will be in touch with any questions.
        </p>
        <a href={`/api/contract-pdf/${token}`}
          className="inline-block mt-5 bg-gold-500 hover:bg-gold-600 text-white px-5 py-2.5 rounded-xl text-sm font-semibold transition-colors">
          Download Signed PDF
        </a>
        <p className="text-sm text-gray-400 mt-4">— Nuno Productions LLC</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-white">
      <div className="bg-gray-900 text-white py-6 px-4 text-center">
        <h1 className="font-serif text-2xl font-bold text-gold-400">Nuno Productions LLC</h1>
        <p className="text-gray-300 text-sm mt-1">Venue Rental Agreement · Los Angeles, California</p>
        {snapshot && (
          <p className="text-gray-500 text-xs mt-1">Terms version {snapshot.termsVersion}</p>
        )}
      </div>

      <div className="max-w-2xl mx-auto px-4 py-8 space-y-6">
        {snapshot
          ? snapshot.sections.map((s) => (
              <ContractSection key={s.num} num={s.num} title={s.title}>
                <SectionBody section={s} />
              </ContractSection>
            ))
          : <p className="text-gray-400 text-sm text-center">Loading contract terms…</p>
        }

        <div className="border border-gray-200 rounded-xl p-4 bg-gray-50">
          <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-3">Owner — Nuno Productions (Pre-signed)</p>
          <div className="space-y-1 text-sm text-gray-700">
            <p><span className="text-gray-500">Printed Name:</span> <strong>{snapshot?.owner?.name ?? 'Justin Nuno'}</strong></p>
            <p><span className="text-gray-500">Company:</span> <strong>{snapshot?.owner?.company ?? 'Nuno Productions LLC'}</strong></p>
          </div>
        </div>

        <div className="border-t-2 border-gold-400 pt-6">
          <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-4">Client Signature Required</p>
          <p className="text-sm text-gray-600 mb-5">
            By signing below, you confirm you have read, understood, and agree to all {snapshot?.sections?.length ?? 14} sections of this Agreement.
          </p>

          <div className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Full Legal Name *</label>
              <input
                type="text"
                value={typedName}
                onChange={e => setTypedName(e.target.value)}
                placeholder="Type your full legal name"
                className="w-full px-4 py-3 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-gold-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Signature * — Draw with finger or mouse</label>
              <div className="border-2 border-gray-300 rounded-xl overflow-hidden bg-white" style={{ height: 160 }}>
                <canvas ref={canvasRef} className="signature-canvas w-full h-full" style={{ width: '100%', height: '100%' }} />
              </div>
              <button type="button" onClick={() => padRef.current?.clear()} className="mt-1 text-xs text-gray-400 hover:text-gray-600 underline">
                Clear signature
              </button>
            </div>

            <label className="flex items-start gap-3 cursor-pointer">
              <input type="checkbox" checked={agreed} onChange={e => setAgreed(e.target.checked)}
                className="mt-0.5 w-4 h-4 accent-gold-500 flex-shrink-0" />
              <span className="text-sm text-gray-700">
                I have read and agree to all {snapshot?.sections?.length ?? 14} sections of this Venue Rental Agreement. I understand this is a legally binding contract.
              </span>
            </label>

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3 rounded-xl">{error}</div>
            )}

            <button
              onClick={handleSubmit}
              disabled={submitting}
              className="w-full py-4 bg-gold-500 hover:bg-gold-600 text-white font-bold rounded-xl transition-colors disabled:opacity-50 text-base"
            >
              {submitting ? 'Signing…' : 'Sign Contract'}
            </button>

            <p className="text-xs text-gray-400 text-center">
              Electronically signing this agreement has the same legal effect as a handwritten signature under California law.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

function SectionBody({ section }: { section: ContractSnapshotSection }) {
  return (
    <div className="space-y-2">
      {section.paragraphs.map((para, i) => {
        if (para.toUpperCase() === para && para.length > 20) {
          return (
            <div key={i} className="bg-red-50 border border-red-200 rounded-lg p-3">
              <p className="text-sm font-bold text-red-800">{para}</p>
            </div>
          );
        }
        if (para.startsWith('Pricing Model —')) {
          const isSelected = para.includes('(selected)');
          return (
            <div key={i} className={`rounded-lg border-2 p-3 ${isSelected ? 'border-gold-500 bg-gold-50' : 'border-gray-200 opacity-50'}`}>
              <div className="flex items-center gap-2">
                <span className="text-base">{isSelected ? '☑' : '☐'}</span>
                <span className="font-semibold text-gray-900 text-sm">{para.replace(' (selected)', '').replace('Pricing Model — ', '')}</span>
              </div>
            </div>
          );
        }
        if (para.match(/^(FLAT RATE|REVENUE SHARE):/)) {
          return <p key={i} className="text-sm text-gray-600 pl-4 border-l-2 border-gray-200">{para}</p>;
        }
        if (para.match(/^(\d+[+–]?\s*days|Less than \d+|14\+|7–|3–)/)) {
          return <p key={i} className="text-sm font-medium text-gray-800 pl-3 border-l-2 border-gold-400">{para}</p>;
        }
        if (para.endsWith(':') && para.length < 60) {
          return <p key={i} className="text-sm font-semibold text-gray-800">{para}</p>;
        }
        return <p key={i} className="text-sm text-gray-700">{para}</p>;
      })}
    </div>
  );
}

function ContractSection({ num, title, children }: { num: string | number; title: string; children: React.ReactNode }) {
  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden">
      <div className="bg-gray-900 text-white px-4 py-2 flex items-center gap-2">
        <span className="text-gold-400 font-bold text-sm">{num}.</span>
        <span className="font-semibold text-sm tracking-wide">{title}</span>
      </div>
      <div className="p-4">{children}</div>
    </div>
  );
}
